import React from "react";
import "./Header.css";

// Hook Imports
import { useLocation } from "../../hooks/useRouter";
import { useDispatch, useSelector } from "react-redux";
import sidebarConfig from "../../config/sidebar.json";
import { useBreadcrumb } from "../../contexts/BreadcrumbContext";
import { useEventEmitter } from "../../contexts/EventEmmiter";
import { setIsGenerated } from "../../store/appActions";

import DebugControls from "./DebugControls";
import { DEBUG_MODE_ENABLED } from "../../constant/globalConstants";

import dashboardIcon from "../../assets/images/dashboard-icon.svg";
import dashboardActiveOrange from "../../assets/images/active-orange-dashboard-icon.svg";
import promptIcon from "../../assets/images/prompt-manager-icon.svg";
import promptActive from "../../assets/images/active-prompt-manager.svg";
import rightArrowIcon from "../../assets/images/right-arrow-icon.svg";

const ICON_MAP = {
  dashboardIcon: {
    default: dashboardIcon,
    hover: dashboardActiveOrange,
  },
  promptIcon: {
    default: promptIcon,
    hover: promptActive,
  },
};

const Header = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const { emit } = useEventEmitter();
  const { breadcrumbs } = useBreadcrumb();

  const isDossierGenerated = useSelector((state) => state?.app?.isGenerated);

  const activeTab = sidebarConfig.sidebarTabs.find(
    (tab) => location.pathname === tab.path
  );

  const iconConfig = activeTab
    ? ICON_MAP[activeTab.icon] || { default: dashboardIcon, hover: dashboardActiveOrange }
    : { default: dashboardIcon, hover: dashboardActiveOrange };

  // Breadcrumbs only show when a dossier is actually generated
  const isDossierActive = location.pathname === "/dashboard" && Boolean(isDossierGenerated);

  const handleDashboardClick = () => {
    dispatch(setIsGenerated(false));
    emit("RESET_DASHBOARD");
  };

  // Determine active breadcrumb items
  const activeBreadcrumbs =
    breadcrumbs && breadcrumbs.length > 0
      ? breadcrumbs
      : isDossierActive
        ? [
          {
            label: activeTab?.label || "Dashboard",
            icon: iconConfig,
            onClick: handleDashboardClick,
          },
          {
            label: "Generated Dossier",
          },
        ]
        : null;

  return (
    <header className="header-root">
      {activeBreadcrumbs ? (
        <nav className="header-breadcrumbs" aria-label="Breadcrumb">
          {activeBreadcrumbs.map((crumb, index) => {
            const isLast = index === activeBreadcrumbs.length - 1;
            const crumbIcon =
              index === 0 ? crumb.icon || iconConfig : crumb.icon;

            return (
              <React.Fragment key={crumb.id || index}>
                {index > 0 && (
                  <img
                    src={rightArrowIcon}
                    alt=">"
                    className="header-breadcrumb-separator"
                    aria-hidden="true"
                  />
                )}
                {isLast ? (
                  <span className="header-breadcrumb-current">
                    {crumb.label}
                  </span>
                ) : (
                  <button
                    type="button"
                    className="header-breadcrumb-link"
                    onClick={
                      crumb.onClick ||
                      (index === 0 ? handleDashboardClick : undefined)
                    }
                    title={`Back to ${crumb.label}`}
                  >
                    {crumbIcon && (
                      <span className="header-breadcrumb-icon-wrapper">
                        <img
                          src={crumbIcon.default || crumbIcon}
                          alt=""
                          className="header-page-icon header-icon-default"
                        />
                        <img
                          src={crumbIcon.hover || crumbIcon.active || crumbIcon.default || crumbIcon}
                          alt=""
                          className="header-page-icon header-icon-hover"
                        />
                      </span>
                    )}
                    <span className="header-breadcrumb-parent-text">
                      {crumb.label}
                    </span>
                  </button>
                )}
              </React.Fragment>
            );
          })}
        </nav>
      ) : (
        <div className="header-title-container">
          {iconConfig && (
            <img
              src={iconConfig.default || iconConfig}
              alt={activeTab?.label || "Page Icon"}
              className="header-page-icon"
            />
          )}
          <span className="header-page-name">
            {activeTab ? activeTab.label : "Dossier"}
          </span>
        </div>
      )}

      <div className="header-right-container">
        {DEBUG_MODE_ENABLED && <DebugControls />}
      </div>
    </header>
  );
};

export default Header;
